import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Trash2, Loader2, Calculator, Save, Building2 } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";

const input = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2 outline-none focus:border-violet text-sm";
const mxn = (v) => (v == null ? "—" : Number(v).toLocaleString("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }));
const blank = () => ({ bank: "Banco", price: 3000000, down_pct: 20, annual_rate: 11, term_years: 20, opening_pct: 1, monthly_insurance: 800, notary_pct: 5 });

const FIELDS = [
  ["bank", "Banco / Escenario", "text"],
  ["price", "Precio del inmueble", "number"],
  ["down_pct", "Enganche (%)", "number"],
  ["annual_rate", "Tasa anual (%)", "number"],
  ["term_years", "Plazo (años)", "number"],
  ["opening_pct", "Comisión apertura (%)", "number"],
  ["monthly_insurance", "Seguro mensual ($)", "number"],
  ["notary_pct", "Gastos notariales (%)", "number"],
];
const ROWS = [
  ["down", "Enganche"], ["loan", "Monto a financiar"], ["monthly_pi", "Mensualidad (capital+interés)"],
  ["monthly_total", "Mensualidad total (con seguro)"], ["interest", "Intereses totales"],
  ["opening_fee", "Comisión por apertura"], ["notary", "Gastos notariales"],
  ["upfront", "Desembolso inicial"], ["total_cost", "Costo total"], ["cat", "CAT aprox. (%)"],
];

export default function GrMortgage() {
  const [scenarios, setScenarios] = useState([blank(), { ...blank(), bank: "Banco B", annual_rate: 10.5 }]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [client, setClient] = useState("");
  const [property, setProperty] = useState("");
  const [saved, setSaved] = useState([]);

  const loadSaved = () => api.get("/gr/mortgage/saved").then((r) => setSaved(r.data)).catch(() => {});
  const [sp] = useSearchParams();
  useEffect(() => {
    loadSaved();
    const price = sp.get("price"); const prop = sp.get("property");
    let init = [blank(), { ...blank(), bank: "Banco B", annual_rate: 10.5 }];
    if (price && Number(price) > 0) { init = init.map((s) => ({ ...s, price: Number(price) })); setScenarios(init); }
    if (prop) setProperty(prop);
    compute(init);
    /* eslint-disable-next-line */
  }, []);

  const compute = async (sc) => {
    setLoading(true);
    try { const r = await api.post("/gr/mortgage/compute", sc.map((s) => ({ ...s, price: Number(s.price), down_pct: Number(s.down_pct), annual_rate: Number(s.annual_rate), term_years: Number(s.term_years), opening_pct: Number(s.opening_pct), monthly_insurance: Number(s.monthly_insurance), notary_pct: Number(s.notary_pct) }))); setResults(r.data); }
    catch (e) { toast.error("Error al calcular"); } finally { setLoading(false); }
  };
  const update = (i, k, v) => { const s = scenarios.map((x, j) => j === i ? { ...x, [k]: v } : x); setScenarios(s); };
  const addScenario = () => setScenarios([...scenarios, { ...blank(), bank: `Banco ${String.fromCharCode(65 + scenarios.length)}` }]);
  const removeScenario = (i) => { const s = scenarios.filter((_, j) => j !== i); setScenarios(s); compute(s); };
  const save = async () => {
    if (!client.trim()) return toast.error("Escribe el nombre del cliente");
    try { await api.post("/gr/mortgage/saved", { client, property, scenarios: scenarios.map((s) => ({ ...s, price: Number(s.price) })) }); toast.success("Escenario guardado"); loadSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const best = results.length ? results.reduce((a, b) => (b.total_cost < a.total_cost ? b : a)) : null;

  return (
    <OSLayout title="OMNIA Mortgage Simulator">
      <div className="space-y-6" data-testid="gr-mortgage-page">
        <div>
          <div className="overline text-navy/40 mb-2 flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> GOÛT READY™ · Sales Suite</div>
          <h2 className="font-editorial text-4xl tracking-tight">OMNIA Mortgage Simulator</h2>
          <p className="mt-2 text-navy/50 max-w-2xl">Simula y compara escenarios hipotecarios reales: mensualidad, enganche, CAT aproximado, gastos y costo total.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          {scenarios.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="card-surface p-4" data-testid={`gr-scenario-${i}`}>
              <div className="flex items-center justify-between mb-3"><span className="overline text-navy/40">Escenario {i + 1}</span>{scenarios.length > 1 && <button onClick={() => removeScenario(i)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>}</div>
              <div className="grid grid-cols-2 gap-2">
                {FIELDS.map(([k, label, t]) => (
                  <div key={k} className={k === "bank" ? "col-span-2" : ""}>
                    <label className="text-[11px] text-navy/50 mb-0.5 block">{label}</label>
                    <input type={t} step="any" className={input} value={s[k]} onChange={(e) => update(i, k, e.target.value)} data-testid={`gr-input-${i}-${k}`} />
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          <button onClick={() => compute(scenarios)} className="btn btn-primary" data-testid="gr-compute"><Calculator className="w-4 h-4" /> Calcular</button>
          <button onClick={addScenario} className="btn btn-outline"><Plus className="w-4 h-4" /> Agregar escenario</button>
        </div>

        {loading ? <div className="py-10 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> :
          results.length > 0 && (
            <div className="card-surface overflow-x-auto" data-testid="gr-results">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-navy/10 text-navy/50"><th className="text-left font-medium px-4 py-3">Concepto</th>{results.map((r, i) => <th key={i} className="text-right font-medium px-4 py-3">{r.bank}{best && r.bank === best.bank && <span className="badge badge-violet ml-2">Mejor</span>}</th>)}</tr></thead>
                <tbody>
                  {ROWS.map(([k, label]) => (
                    <tr key={k} className={`border-b border-navy/5 last:border-0 ${k === "monthly_total" || k === "total_cost" ? "font-medium" : ""}`}>
                      <td className="px-4 py-2.5 text-navy/60">{label}</td>
                      {results.map((r, i) => <td key={i} className={`px-4 py-2.5 text-right ${(k === "monthly_total" || k === "total_cost") ? "text-violet" : ""}`} data-testid={`gr-cell-${i}-${k}`}>{k === "cat" ? `${r[k]}%` : mxn(r[k])}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        <div className="card-surface p-4">
          <div className="overline text-navy/40 mb-3 flex items-center gap-1.5"><Save className="w-3.5 h-3.5" /> Guardar para el cliente</div>
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[160px]"><label className="text-[11px] text-navy/50 mb-0.5 block">Cliente</label><input className={input} value={client} onChange={(e) => setClient(e.target.value)} data-testid="gr-client" /></div>
            <div className="flex-1 min-w-[160px]"><label className="text-[11px] text-navy/50 mb-0.5 block">Propiedad</label><input className={input} value={property} onChange={(e) => setProperty(e.target.value)} /></div>
            <button onClick={save} className="btn btn-primary" data-testid="gr-save"><Save className="w-4 h-4" /> Guardar</button>
          </div>
          {saved.length > 0 && <div className="mt-4 space-y-1.5">{saved.map((sv) => (
            <div key={sv.id} className="flex items-center justify-between text-sm border-b border-navy/5 py-1.5" data-testid={`gr-saved-${sv.id}`}>
              <span><b>{sv.client}</b>{sv.property ? ` · ${sv.property}` : ""} <span className="text-navy/40">({sv.results?.length} escenarios · desde {mxn(Math.min(...sv.results.map((r) => r.monthly_total)))}/mes)</span></span>
              <button onClick={async () => { await api.delete(`/gr/mortgage/saved/${sv.id}`); loadSaved(); }} className="text-navy/25 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}</div>}
        </div>
      </div>
    </OSLayout>
  );
}
